import { FormControl, MenuItem, Select, type SelectChangeEvent } from "@mui/material"
import type { FunctionComponent } from "react"
import { memo } from "react"
import type { Control } from "react-hook-form"
import { useController } from "react-hook-form"

import { useGetLocationAssociation } from "./Product/Information/hooks/useGetLocationAssociation"
import type { ProductAddFormValues } from "./productFormSchema"

import { FormField } from "@/components/Forms"
import { SELECT_STYLING_PROPS } from "@/constants/forms"
import type { Location, ProductLocationAssociationInput } from "@/graphql/codegen/graphql"
import { AddressFormat } from "@/types/common"
import { formatLocationAddress } from "@/utils/utils"

interface ProductShipLocationSelectProps {
  name: "locationsAssociations"
  control: Control<ProductAddFormValues>
}

export type LocationAssociation = Location & {
  locationAssociationId: string
}

const ProductShipLocationSelect: FunctionComponent<ProductShipLocationSelectProps> = ({ name, control }) => {
  const { field: companyIdField } = useController({
    name: "companyId",
    control,
  })

  const { locationsAssociationsList } = useGetLocationAssociation({
    companyId: companyIdField.value ?? "",
  })

  const options = locationsAssociationsList.map(({ companyLocationAssociationId, shipping }) => ({
    value: companyLocationAssociationId,
    label: formatLocationAddress(shipping, AddressFormat.Shipping),
  }))

  const handleSingleChange = ({ target: { value: locationAssociationId } }: SelectChangeEvent, field: any) => {
    const selected =
      locationsAssociationsList
        .filter(({ companyLocationAssociationId }) => locationAssociationId === companyLocationAssociationId)
        .map(({ companyLocationAssociationId, shipping }) => {
          return {
            locationAssociationId: companyLocationAssociationId,
            address1: shipping?.address1,
            city: shipping?.city,
            name: shipping?.name,
            region: shipping?.region,
            description: shipping?.description,
          }
        }) ?? []

    field.onChange(selected)
  }

  return (
    <FormField
      name={name}
      label='Product Ship From Location'
      render={({ field, fieldState: { error } }) => {
        if (!field.value) return null

        const selectedLocations = (field.value as ProductLocationAssociationInput[])
          .map((val) => val.locationAssociationId)
          .filter(Boolean)

        return (
          <FormControl fullWidth>
            <Select<string>
              {...field}
              {...SELECT_STYLING_PROPS}
              error={!!error}
              value={selectedLocations[0] || ""}
              onChange={(e) => handleSingleChange(e, field)}
            >
              <MenuItem value=''>
                <span className='text-gray-400'>Select One</span>
              </MenuItem>
              {options.map(({ value, label }) => (
                <MenuItem key={`option-${value}`} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )
      }}
    />
  )
}

export default memo(ProductShipLocationSelect)
